import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import EditIcon from '@mui/icons-material/Edit';
import { timeTrackingApi, TimeEntry } from '../../api/endpoints/timeTrackingApi';

interface TaskTimerProps {
  taskId: string;
}

export const TaskTimer: React.FC<TaskTimerProps> = ({ taskId }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [currentEntry, setCurrentEntry] = useState<TimeEntry | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [description, setDescription] = useState('');
  const [showDescriptionInput, setShowDescriptionInput] = useState(false);

  const checkRunningTimer = useCallback(async () => {
    try {
      const runningTimer = await timeTrackingApi.getRunningTimer();
      if (runningTimer && runningTimer.taskId === taskId) {
        setIsRunning(true);
        setCurrentEntry(runningTimer);
        setDescription(runningTimer.description || '');

        // Calculate elapsed time
        const start = new Date(runningTimer.startTime).getTime();
        const now = Date.now();
        setElapsed(Math.floor((now - start) / 1000));
      }
    } catch (error) {
      console.error('Failed to check running timer:', error);
    }
  }, [taskId]);

  useEffect(() => {
    checkRunningTimer();
  }, [checkRunningTimer]);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isRunning && currentEntry) {
      interval = setInterval(() => {
        const start = new Date(currentEntry.startTime).getTime();
        const now = Date.now();
        setElapsed(Math.floor((now - start) / 1000));
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning, currentEntry]);

  const handleStart = async () => {
    try {
      const entry = await timeTrackingApi.startTimer(taskId, description);
      setCurrentEntry(entry);
      setIsRunning(true);
      setElapsed(0);
      setShowDescriptionInput(false);
    } catch (error) {
      console.error('Failed to start timer:', error);
      alert('Failed to start timer. You may already have a timer running on another task.');
    }
  };

  const handleStop = async () => {
    if (!currentEntry) return;

    try {
      await timeTrackingApi.stopTimer(currentEntry.id);
      setIsRunning(false);
      setCurrentEntry(null);
      setElapsed(0);
      setDescription('');
    } catch (error) {
      console.error('Failed to stop timer:', error);
    }
  };

  const formatTime = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">Time Tracking</Typography>
        {!isRunning && (
          <Tooltip title="Add description">
            <IconButton
              size="small"
              onClick={() => setShowDescriptionInput(!showDescriptionInput)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {showDescriptionInput && !isRunning && (
        <TextField
          fullWidth
          size="small"
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 2 }}
          placeholder="What are you working on?"
        />
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography
          variant="h3"
          sx={{
            fontFamily: 'monospace',
            fontWeight: 'bold',
            color: isRunning ? 'primary.main' : 'text.secondary',
          }}
        >
          {formatTime(elapsed)}
        </Typography>

        {isRunning ? (
          <Button
            variant="contained"
            color="error"
            startIcon={<StopIcon />}
            onClick={handleStop}
            size="large"
          >
            Stop
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            startIcon={<PlayArrowIcon />}
            onClick={handleStart}
            size="large"
          >
            Start
          </Button>
        )}
      </Box>

      {isRunning && description && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="textSecondary">
            {description}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default TaskTimer;
